import {React, _, utils} from '@api'
import {Classes} from '@blueprintjs/core'
import {BuilderCardContextMenuItems, DOUBLE_CLICK_MS, GroupByGroupStore, BuilderCard, CatalogItemDetailsStore, IContextMenuItems, GroupByGroupStoreContext, VipLocalStore, BuilderCardImageStore, ICatalogItemDetailsPathElement, MyBuilderCardImageStore, AssortmentCardStore, DEBUG_CATALOG_ITEM_CARDS, BuilderCardFieldStore} from '@modules'
import {CatalogItemShapeStore, WhiteboardStore} from '@modules/whiteboard'
import {CARD_CREATE_SIZE_SCALING_FACTOR} from '@modules/whiteboard/WhiteboardCreateShapesStore'
import {ICatalogFieldConfig, nav, site, LibraryItemConfig, DragDropEventHandlers, OFFLINE_ID, StackView, CatalogPerspectiveConfig, WorkspaceConfig, ui, CatalogCardImagesView, ImageRecord, CATALOG_ID_SEPARATOR, StoresInstance, IDragDropElement, CatalogFieldName, FilterLabel} from '@stores'
import {BuilderCardLocalData, ICatalogItemLocalData} from '@stores/catalog/ICatalogItemLocalData'
import {DragDropStore} from '@stores/dragDrop'
import {C8ImageSize} from '@utils'
import {CardSizeType, MyImageStore, StyledProps} from 'centric-ui-components'
import {action, computed, observable, runInAction, toJS, autorun, reaction} from 'mobx'
import {computedFn} from 'mobx-utils'
import {useContext} from 'react'
import * as css from './BuilderCard.css'

export const BuilderCardStoreContext = React.createContext<BuilderCardStore>(undefined)
export const useBuilderCardStore = () => useContext(BuilderCardStoreContext)

export type TargetRenderer = 'web' | 'pdf'
export type CardImageData = { image: BuilderCardImageStore, imageUrl?: { defaultImageUrl: string, highDefUrl: string }, imageId?, qrCodeUrl?, filename?, id, __parent?, __path? }

export interface ImageUrlWithHD {
  defaultImageUrl?: string,
  highDefUrl?: string
}

export interface BuilderCardProps extends StyledProps {
  onSetCardStore?: (store: BuilderCardStore) => void
  data: any & { images: Array<any> } & ICatalogItemLocalData
  itemDetailsStore?: CatalogItemDetailsStore
  library?: LibraryItemConfig
  type?: string // What is this?  An MPV?  A style? etc

  noFocus?: boolean
  noSelect?: boolean
  /**
   * A popover for a card in the main catalog
   */
  inlinePopover?: boolean
  isPopover?: boolean
  popoverParent?: 'searcher' | 'catalog'

  showImage?: boolean

  /** Don't attach a hover listener **/
  noHover?: boolean
  /* On Duplicate, make card fields read-only */
  readOnly?: boolean
  //cardViewData: CardViewConfig & any
  className?: string
  imageSource?: string
  onClickNotes?: (e) => void
  onClickMoreMenu?: (e) => void,
  onDoubleClick?: (e) => void
  groupByHierarchy?: string[]
  buildImageUrl?: (imageWrapper?: { image }, size?: string) => string
  size?: CardSizeType
  onClickProduct?: (e: React.MouseEvent, id, items?: Array<string>, stackView?: StackView, group?: GroupByGroupStore) => void
  path?: string[]
  renderer?: TargetRenderer
  scrollContainer?: HTMLElement
  levelOfDetail?: number
  hideButtons?: boolean
  placeholderText?: string
  visibleCardFields?: Array<ICatalogFieldConfig>,
  disableSelection?: boolean
  /** For multiple cards shown in a single subfield, like article candidates **/
  inline?: boolean

  /**
   * An MPV can point to a 'specification item' such as a color or material
   * So we have something like Superman (MPV) -> Oxford Shirt (Style)
   *    When rendering the 'Oxford Shirt' card as a field within the MPV card the 'Oxford Shirt' JSON object would contain all of that record's attributes as well as potentially links to other complex objects (such as Style -> Colorways)
   *
   * Something like { field: { field: 'color', esIndex: 'vlp-color'}, data: { id: 'someColorId', name: 'red'}, parent: { _id: 'someMpv'}}
   */
  nested?: {
    /**
     * This can be a card rendered as say a list of colorways in a field on a card OR we can have pivoted to the colorway perspective and now it's a full fledged colorway card
     */
    isInCardField?: boolean
    arrayIndex?: number,
    field?: ICatalogFieldConfig,
    data?: Partial<{ id: string, images?: Array<{ exId?: string, exUpdatedAt?: string, exCreatedAt?: string }> }> & any,
    /** For xref / nested card items we show a faked builder card but when toggling details we need to include the parent in our path **/
    parentData?: { _id?: string }
    parentField?: ICatalogFieldConfig
  }

  targetSidebar?: 'left' | 'right'

  /**
   * Display a specific image instead of the first one found in the imageSource
   */
  image?: any

  /**
   * Position in parent container
   */
  index?: number

  // Todo - just pass the ID so we don't have to observe all this
  workspace?: Partial<WorkspaceConfig>
  showListPreviewCard?: boolean
}

export class BuilderCardStore extends VipLocalStore implements IContextMenuItems {
  private changeLODTimeout: number
  @observable fieldsAreOnScreen = false
  components: {fields: Record<CatalogFieldName, BuilderCardFieldStore>} = {fields: {}}

  /**
   * Prevent firing drag events if an MPV is dragging and it's not us to avoid stealing drag focus (DragLeave event) during drag over product card in group
   */
  @computed get isDragItem() {
    const {dragDrop} = this
    return !dragDrop.isDragging || dragDrop.dragType !== this.dragDropEventHandlers.def.dragType || dragDrop.dragItem.id !== this.idWithPath ? undefined : true
  }

  get data() {
    const {isOffline, id, catalog} = this
    const r = this.detailsStore?.data ?? (isOffline ? catalog.offlineItemsMap[id]?.item?.data: null) ?? this.propsData

    return r
  }

  get dataMap() {
    return this.workspace?.dataMap ?? this.catalog.dataMap
  }

  get detailsStore() { return this.otherStores.detailsStore}

  get groupByStore() { return this.otherStores.groupByStore}

  get assortmentCard() { return this.otherStores.assortmentCard}

  get shapeStore() { return this.otherStores.shapeStore}

  @observable propsData
  constructor(stores: StoresInstance, public props: BuilderCardProps, public otherStores: { detailsStore?: CatalogItemDetailsStore, groupByStore?: GroupByGroupStore, assortmentCard?: AssortmentCardStore, shapeStore?: CatalogItemShapeStore }) {
    super(stores)
    this.propsData = _.cloneDeep(props.data)

    const {catalog, props: {type, data}} = this
    this.globalLevelOfDetail = catalog.settings.levelOfDetail

    this.dragDropEventHandlers = new DragDropEventHandlers(catalog, {
      id: this.idWithPath,
      dragType: type,
      target: catalog.currentIndex,
      label: _.get(data, catalog.dataMap.title) || catalog.catalogItemName,
      filename: _.get(data, catalog.dataMap.title) || catalog.catalogItemName,
      imageUrl: this.imageUrl?.highDefUrl,
      targetExtra: {store: this, data},
      ignoreEvents: () => this.isCardFieldDragging,
    })

    if (_.isNumber(this.localData?.lod) || props.isPopover) { // After toggling local lod the mouse will already be inline and won't trigger buttons to show otherwise
      if (!props.hideButtons) {
        this.hideButtons = false
        this.everMousedOver = true
      }
    }

    if (props.isPopover) {
      this.fieldsEverVisible = true
    }

    props.onSetCardStore && props.onSetCardStore(this)
  }

  @computed get index() {
    return this.catalog.index
  }

  @computed get href() {
    return nav.urlForObject(this.data)
  }

  @observable.ref selectedNestedItem: any

  /// region Context Menu

  @computed get contextItemHref() {
    const {contextItem, contextItemField, href, catalog} = this
    if (!contextItemField?.esIndex) return href

    const ws = catalog.workspaceByIndex[contextItemField.esIndex]
    if (!ws) return href

    return nav.urlForWorkspace({workspace: ws, itemId: catalog.idFor(contextItem.data, false)}, true)
  }

  @computed get hasImage() {
    const {imageUrl, contextItem} = this
    return !_.isEmpty(contextItem?.imageUrl ?? imageUrl)
  }

  get contextMenuItems() {
    return <GroupByGroupStoreContext.Provider value={this.groupByStore}>
      <BuilderCardStoreContext.Provider value={this}>
        <BuilderCardContextMenuItems/>
      </BuilderCardStoreContext.Provider>
    </GroupByGroupStoreContext.Provider>
  }

  /// endregion

  @observable everSelected = false
  @observable isShowingContextMenu = false

  @computed get matchedPerspectives(): Array<CatalogPerspectiveConfig> {
    return this.catalog.perspectives.filter(p => p.matchCatalogItem && p.matchCatalogItem(this.props.data, {stores: this.stores}))
  }

  @computed get matchedPerspectivesAttrs() {
    return this.matchedPerspectives.reduce((acc, p) => ({...acc, [`data-perspective-${p.id.replaceAll('_', '-')}`]: 'true'}), {})
  }

  @computed get rootStyle() {
    const {imagesToRender, props: {isPopover}} = this
    return {
      '--image-groups': _.uniqBy(imagesToRender ?? [], (img, i) => img.image?.__path ?? i).length,
      '--image-count': imagesToRender?.length ?? 0,
      '--catalog-image-background': isPopover ? this.catalog.imageBackgroundStyle : undefined
    } as any
  }

  @computed get cardStyle() {
    const {props: {style}, drilldown, objectFit, perspectiveStyle, stackedImagesStyle} = this
    return {
      ...style,
      '--drilldown-count': drilldown?.items?.length,
      '--image-source-object-fit': objectFit,
      ...perspectiveStyle,
      ...stackedImagesStyle
    } as any
  }

  @computed get perspectiveStyle() {
    const {matchedPerspectives} = this
    if (matchedPerspectives.length) {
      return {'--perspective-border-color': matchedPerspectives[0].background}
    }

    return {}
  }

  @observable globalLevelOfDetail: number

  @computed get levelOfDetail() {
    const {
      localLOD, props, props: {isPopover, levelOfDetail},
      globalLevelOfDetail: lodSettings
    } = this

    const r = isPopover ? 1 : props.levelOfDetail === 0
              ? 0
              : _.isNumber(localLOD)
                ? localLOD
                : lodSettings === 1
                  ? 1
                  : (localLOD ?? levelOfDetail ?? lodSettings)

    //console.log({r, isPopover, props: props.levelOfDetail, lodSettings, localLOD, id: this.id})
    return r
  }

  @computed get hasInitializedLOD() {
    return !_.isUndefined(this.globalLevelOfDetail)
  }

  registerEffects = () => {
    const {catalog} = this
    if (this.props.isPopover) {
      catalog.componentStores.currentCardPopover = this
    }

    if (!this.props.isPopover) {
      catalog.componentStores.cards[this.idWithPath] = this
    }

    const toDispose = [
      // autorun(() => {
      //   if (this.data?.images) {
      //     const images = this.data?.images?.slice()
      //     runInAction(() => {
      //       console.log(`images changed`, images)
      //       this.selectedImageIndex = 0
      //       this.selectNextLoadedImage = true
      //     })
      //   }
      // }, {name: `Reset image carousel when images list changes`}),
      autorun(() => {
        const {groupByStore} = this
        this.globalLevelOfDetail = undefined
      }),
      autorun(() => {
        const {groupByStore, hasInitializedLOD, updateLocalData, localData} = this,
          contextLOD = groupByStore?.levelOfDetail ?? catalog.settings.levelOfDetail

        runInAction(() => {
          // Set initial value
          if (!hasInitializedLOD || contextLOD === 0) {
            this.changeLODTimeout && window.clearTimeout(this.changeLODTimeout)
            this.globalLevelOfDetail = contextLOD
          } else { // can get stuck when user toggles back and forth fast
            // Stagger changes to lod 1
            if (this.globalLevelOfDetail != contextLOD) {
              // if (ui.settings.staggerLevelOfDetail) {
              //   this.changeLODTimeout = window.setTimeout(() => {
              //     // console.log(`updating LOD for card ${this.id}`)
              //     this.globalLevelOfDetail = contextLOD
              //     if (contextLOD == 1 && this.localLOD === 0) {
              //       localData.lod = undefined
              //     }
              //   }, 100 * (this.props.index ?? 1))
              // }
              // else {
                this.globalLevelOfDetail = contextLOD
                if (_.isNumber(localData?.lod) && localData?.lod !== contextLOD) {
                  localData.lod = undefined
                }
              // }
            }
          }
        })
      }, {name: `Track previous global level of detail so we can stagger updates`}),
      reaction(() => this.localData?.lod, (lod) => {
        if (this.hasInitializedLOD && lod !== undefined) {
          const {groupByStore} = this
          if (groupByStore?.settings?.levelOfDetail !== undefined) {
            const {settings: {levelOfDetail: groupLOD}} = groupByStore

            // Reset the group LOD iff all cards inside are the wrong LOD now
            if (groupLOD !== lod && groupByStore.builderCardStores.every(v => v.localLOD === lod)) {
              // Clear the parent store's LOD, everyone has been flipped the opposite way
              groupByStore.settings.levelOfDetail = lod
            }
          }
        }
      }, {name: `Update parent group LOD settings if user toggles a card in the group and it invalidates the LOD above`}),
      autorun(() => {
        this.allImagesLoaded = this.imagesToRender.every(img => img.imageStore?.loaded)
      }, {name: `Delay registering all images loaded to avoid animation artifacts on first load in as widths/positions change`, delay: 10}),
      /*autorun(() => {
        // trace()
        const {props: {data}, detailsStore} = this
        detailsStore?._assignConstrainedValues()
      }, {name: 'Assign constrained values when other values change (style for a changed colorway for example)'})*/
      autorun(() => {
        if (!this.everSelected && catalog.selection.selectedIds.has(this.idWithPath)) {
          this.everSelected = true
        }
      }, {name: `Builder card reaction for 'have I ever been selected?' - needed for drag and drop image support`}),
      autorun(() => {
        const {selectedImageIndex, images} = this
        if (!_.isEmpty(images) && selectedImageIndex >= images.length) {
          this.selectedImageIndex--
        }
      }, {name: `Reset selected image index when invalid`})
      /*reaction(() => {
        const {props: {data}, detailsStore} = this
        return {
          data, detailsStore
        }
      }, ({detailsStore, data}) => {
        // Added null check. Was showing error on hover of style/candidates in builder card
        if(detailsStore) {
          const {isConstraintDirty, _assignConstrainedValues} = detailsStore
          _assignConstrainedValues(!isConstraintDirty)
        }
      })*/
    ]

    return () => {
      toDispose.forEach(d => d())

      if (!this.props.isPopover) {
        delete catalog.componentStores.cards[this.idWithPath]
      }

      if (this.isHoverPopoverItem) {
        catalog.hoverPopoverItemId = null
      }
    }
  }

  @computed get isHoverPopoverItem() {
    return this.idWithPath === this.catalog.hoverPopoverItemId
  }

  // onSetRef = (ref: HTMLDivElement) => {
  //   this.ref = ref
  //   if (ref && catalog.savingItemId == this.idWithPath) {
  //     scrollIntoView(ref)
  //     catalog.savingItemId = null
  //   }
  // }

  ref: HTMLDivElement

  imageStores: { [id: string]: BuilderCardImageStore } = {}

  @computed get isInWhiteboard() {
    return this.shapeStore != null
  }

  @action imageStoreFor = (image: ImageRecord, i) => {
    const id = image.exId ?? image.file?.lastModified ?? i
    const {imageStores} = this
    if (!imageStores[id]) {
      imageStores[id] = new BuilderCardImageStore(image, i, this)
    } else {
      const r = imageStores[id]
      r.image = image
      r.index = i
    }

    return imageStores[id]
  }

  @action onSaveChanges = async () => {
    const {detailsStore: {applyMutations}} = this
    //catalog.savingItemId = idWithPath
    await this.detailsStore.applyMutations() // If we refetch we will lose all outstanding locally edited changes
  }

  @action onCancelChanges = () => {
    this.detailsStore.cancelMutations()
  }

  element: HTMLDivElement
  @observable everVisible = true // set to false to experiment with intersection visible and avoid loading images early

  @observable everMousedOver = false

  /*private buttonsTimer = 0
  @observable hideButtonsDueToHoverTimeout = false

  private _hideButtonsOnHover_start = () => {
    if (!this.props.isPopover && !this.buttonsTimer) {
      this.buttonsTimer = setTimeout(() => {
        this.buttonsTimer = 0
        this.hideButtonsDueToHoverTimeout = true
      },  ui.settings.cardPopover.hideHoverButtonsMs)
    }
  }

  private _hideButtonsOnHover_cancel = () => {
    clearTimeout(this.buttonsTimer)
    this.buttonsTimer = 0
  }*/

  @computed get canDuplicate() {
    return this.catalog.isActionButtonEnabled('duplicate', this.data)
  }

  @computed get canDelete() {
    return this.catalog.isActionButtonEnabled('delete', this.data)
  }

  /*onMouseEnter_ImageContainer = (e: React.MouseEvent) => {
    this.hideButtonsDueToHoverTimeout = false
    this._hideButtonsOnHover_start()
  }
  onMouseLeave_ImageContainer = (e: React.MouseEvent) => {
    this._hideButtonsOnHover_cancel()
  }*/

  onMouseEnter = (e: React.MouseEvent) => {
    // console.log(`onMouseEnter`, this.idWithPath)
    if (!this.dragDrop.isDragging) {
      if (!this.props.hideButtons) {
        this.hideButtons = false
      }

      this.everMousedOver = true

      const {groupByStore} = this

      // if (idWithPath === catalog.hoverPopoverItemId) {
      //   catalog.cancelHideHoverPopover()
      // }

      if (groupByStore) {
        groupByStore.setContextualHoverCatalogItem(this)
      }
    }
  }

  onMouseLeave = (e: React.MouseEvent) => {
    // if (catalog.hoverPopoverItemId == this.idWithPath) {
    //   //console.log(`onMouseLeave`, this.idWithPath)
    //   // Comment in if show popover is changed back to being hover instead of click
    //   catalog.waitHideHoverPopover()
    // }

    const {groupByStore} = this
    if (groupByStore?.contextualHoverCatalogItem === this) {
      groupByStore.setContextualHoverCatalogItem(null)
    }
  }

  // _reactionForDragEnter = reaction(() => { return { id: this.id, dragOver: dragDrop.dragOverItem } }, ({ id, dragOver }) => {
  //   if (dragOver) {
  //     const has = this._selectedIds.has(dragOver?.id) && this._selectedIds.has(id) //Array.from(this._selectedIds).find(i => (i === dragOverItem?.id && i === this.id))
  //     if (has) {
  //       console.log('reaction onDragEnter')
  //       this.dragDropEventHandlers.onDragEnter()
  //     }
  //   }
  // })

  @computed get _selectedIds() {
    const {selection: {selectedIds}} = this.catalog
    return selectedIds
  }

  @computed get _intersectSelectedState() {
    const {_selectedIds, dragDrop} = this
    return Array.from(_selectedIds.keys()).find((id: string) => !_.isEmpty(dragDrop.ddHandlers.get(id).state))
  }

  @computed get _selectedState() {
    const {_intersectSelectedState, dragDrop} = this
    return _intersectSelectedState ? dragDrop.ddHandlers[_intersectSelectedState]?.state : null
  }

  get dragDropState() { return this.dragDrop.ddHandlers.get(this.idWithPath)?.state }

  dragDropEventHandlers: DragDropEventHandlers

  onCardDragStart = (e: React.DragEvent) => {
    this.dragDropEventHandlers.onDragStart(e, {setDragImage: utils.isFirefox})
  }

  // @computed get dragDropEventHandlers() {
  //   const {id, data, data: {exId}, props: {type}, imageUrl} = this
  //   trace()
  //   return new DragDropEventHandlers({id: exId || id, dragType: type, target: catalog.currentIndex, filename: _.get(data, catalog.dataMap.title)})
  // }

  onDragStart = (e: React.DragEvent) => {
    // Remove the buttons from the DOM so they don't appear on the drag placeholder
    (e.target as HTMLElement).parentElement.querySelectorAll('[data-type=\'IconButtonWrapper\']').forEach((v: HTMLDivElement) => v.style.visibility = 'hidden')

    this.dragDropEventHandlers.events.onDragStart(e)
  }

  // onDragEnd = (e: React.DragEvent) => {
  //   // Remove the buttons from the DOM so they don't appear on the drag placeholder
  //   (this.cardElement).querySelectorAll("[data-type='IconButtonWrapper']").forEach((v: HTMLDivElement) => v.style.visibility = null);
  //
  //   this.dragDropEventHandlers.events.onDragEnd(e)
  // }

  get sidebar() { return this.stores.sidebar}

  onTouchDoubleTap = (e: React.TouchEvent) => {
    //stores.product.selected.clear()
    e.stopPropagation()
    const {props: {onDoubleClick}, id, sidebar} = this
    onDoubleClick && onDoubleClick(e)

    _.last(sidebar.right.path).where[0] = {value: id.toString()}
  }

  // If they double-click within a short-enough timespan don't do the selection logic
  private _clickTimer = null

  /**
   * User clicked on a catalog item card which may or may not be the original catalog item or it might be a perspective child card (or a child of a child if perspective)
   * @param e
   * @param onClickImageProps
   */
  onClick = (e, onClickImageProps?: { isDoubleClick?: boolean, name?: string, library?: LibraryItemConfig, id: string, imageId?: string, imageWorkspace?: string, imageStore: BuilderCardImageStore }) => {
    if (this.isInWhiteboard) return

    //DEBUG && console.debug(`onClick`, {path: onClickImageProps?.imageStore?.path.slice(), _ignoreNextMouseClickEvent: this._ignoreNextMouseClickEvent, _clickTimer: this._clickTimer, ...(onClickImageProps ?? {})})
    const {_ignoreNextMouseClickEvent} = this

    if (e && !_ignoreNextMouseClickEvent) {
      if (!onClickImageProps?.isDoubleClick && this._clickTimer) return
    }

    if (onClickImageProps?.isDoubleClick) {
      const {catalog, sidebar, flexLayout: {isMaximized}, stores: {flexLayoutCatalogRoot, catalogTab}} = this
      if (onClickImageProps && (onClickImageProps.isDoubleClick || sidebar.right.open)) {
        const {library, imageStore} = onClickImageProps

        let path = imageStore.path.filter(f => !f.catalogField || !catalog.fieldByName(f.catalogField)?.card?.noOpenNestedImage)
        if (library) {
          catalogTab.tab.openCatalogItemDetailsInNewTab({
            path,
            workspace: catalog.workspaceConfig._id,
            parentTab: flexLayoutCatalogRoot.tab.id
          })
        } else {
          catalogTab.tab.openCatalogItemDetailsInNewTab({
            path,
            workspace: catalog.workspaceConfig._id,
            parentTab: flexLayoutCatalogRoot.tab.id
          })
        }
        //
        //   sidebar.right.navigateToLibrary({...onClickImageProps, libraryId: library.id, where: [{value: id}], imageId, name, imageWorkspace}, true)
        // } else {
        //   sidebar.right.navigateToLibrary({...onClickImageProps, where: [{value: this.id}], imageId, name, imageWorkspace}, true)
      }

      return
    }

    const {
      props: {onClickProduct}, idWithPath, groupByStore, id: myId,
      catalog: {selection: {selectedIds, onClickItem}}
    } = this

    if (idWithPath) {
      runInAction(`Clicked on catalog item ${idWithPath}`, () => {
        e.stopPropagation()
        e.persist()

        const selectCard = () => {
          this._clickTimer = null;
          (onClickProduct || onClickItem)(e, idWithPath, null, null, groupByStore)
        }

        if (!selectedIds.has(idWithPath)) {
          selectCard()
        } else {
          this._clickTimer = setTimeout(selectCard, DOUBLE_CLICK_MS)
        }
      })
    }
  }

  /**
   * Both double click and single click will fire on same element, ignore the second single click if a double click is detected
   * @private
   */
  private _ignoreNextMouseClickEvent = false

  onDoubleClick = (e: React.MouseEvent) => {
    const {_clickTimer, props: {onDoubleClick, nested}, id, catalog: {catalogItemName}} = this

    !nested && console.log(`Double-clicked within 'catalogItemName' ${id}`)

    if (_clickTimer) {
      clearTimeout(_clickTimer)
      this._clickTimer = null
      this._ignoreNextMouseClickEvent = true
      setTimeout(() => this._ignoreNextMouseClickEvent = false, 10)

      runInAction(`Double-clicked on '${catalogItemName}' ${id}`, () => {
        onDoubleClick && onDoubleClick(e)
        /**
         * Usually this is handled by an image store but for placeholders we still need to toggle the item
         */
        !nested && this.toggleCatalogItemDetails(e, true)
        //right.updateState({open: true, path: [{libraryItemId: id}]})
      })

      e.stopPropagation()
    } else {
      //console.trace('Ignoring double click due to click event already firing')
    }
  }

  @observable.ref drilldown ?: { field?: ICatalogFieldConfig, items?: Array<any>, parent?: any } = null
  @observable showDrilldown = false

  @action onDrillDown = (drilldown: { field?: ICatalogFieldConfig, items?: any[], parent?: any }) => (e: React.MouseEvent) => {
    if (!drilldown) {
      this.showDrilldown = false
    } else {
      this.drilldown = {...drilldown, items: _.isArray(drilldown.items) ? drilldown.items : [drilldown.items]}
      this.showDrilldown = true
    }

    e.stopPropagation()
    e.preventDefault()
  }

  @computed get isShowingDetails() {
    const {sidebar} = this
    return sidebar.right.open && sidebar.right.path.some(p => p.where && p.where[0]?.value == this.id)
  }

  @action openCatalogItemDetails = (e: React.MouseEvent | React.PointerEvent, location?: 'modal' | 'sidebar') => {
    this.isInWhiteboard && this.shapeStore.whiteboard.store.setAsActiveTab()

    if (location) {
      this.catalog.settings.detailLoc = location
    }
    this.toggleCatalogItemDetails(e, true, false)
  }

  @action toggleCatalogItemDetails = (e: React.MouseEvent | React.PointerEvent, open?: boolean, openImageSlider?: boolean, specificLocation?: 'modal' | 'sidebar') => {
    DEBUG_CATALOG_ITEM_CARDS && console.debug(`toggleCatalogItemDetails`, {open, openImageSlider})

    const {
      stores, stores: {catalogTab, flexLayoutCatalogRoot, flexLayout: {activeTab, isMaximized}, sidebar: {right}}, catalog, catalog: {perspective, aggregation: {closeAggregateDetails, isShowingAggregateDetails}},
      id, esIndex, idWithPath, isShowingDetails, props: {nested, library, data}, cardTitle
    } = this

    let path: Array<ICatalogItemDetailsPathElement>

    if (nested?.data) {
      path = [{where: [{value: data._id}], idWithPath}, {where: [{value: this.id}], esIndex, sidebarContext: 'xref'}]
    } else if (nested?.parentData && library) {
      //console.log({parent, library, data, nestedObject})
      path = [{where: [{value: catalog.idFor(nested?.parentData)}]}, {where: [{value: this.id}], libraryId: library.id, esIndex: library.index, sidebarContext: 'xref'}]
    } else {
      path = [{where: [{value: id}], esIndex, idWithPath}]
    }

    if (!nested?.data && !(nested?.parentData && library)) {
      catalog.focusedCatalogItemId = idWithPath
      // They clicked on this specific catalog item
      e && this.onClick(e)
    }

    if ((flexLayoutCatalogRoot && !isMaximized && !openImageSlider) || (activeTab?.component === 'whiteboard')) {
      if (open === false) {
        stores.extra?.tab?.removeFromParent()
      } else {
        if (!isMaximized && specificLocation === 'sidebar') {
          const {flexLayout: {toggleKnownTab, isShowingCatalogItemDetailsTab, knownTabs: {catalogItemDetailsTab}}} = this
          if (!isShowingCatalogItemDetailsTab) {
            toggleKnownTab('catalogItemDetails', true)
          }
        }
        else {
          (catalogTab?.tab ?? activeTab).openCatalogItemDetailsInNewTab({path, name: cardTitle})
        }
      }
    } else {
      right.updateState({open: open || !isShowingDetails, showImageFullscreen: openImageSlider})

      if (right.open) {
        right.path = path.slice()
        isShowingAggregateDetails && closeAggregateDetails()
      }
    }

    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
  }

  // @computed get isPopoverCatalogItem() {
  //   return this.idWithPath === catalog.hoverPopoverItemId
  // }

  /**
   * To update a tooltip to let the user know when the item will be shown or hidden
   */
  @observable showingOrHidingPopoverInMs: number

  @computed get isShowingPopover() { return this.catalog.hoverPopoverItemId === this.idWithPath }

  @action togglePopover = () => {
    const {catalog} = this
    catalog.hoverPopoverItemId = catalog.hoverPopoverItemId === this.idWithPath ? null : this.idWithPath

    DEBUG_CATALOG_ITEM_CARDS && console.log(`toggle popover - showing:  ${catalog.hoverPopoverItemId}`)
  }

  @computed get showDetailsPopoverSettings() {
    return ui.settings.cardPopover
  }

  private _openPopoverTooltipTimeout: number
  @action onPopoverTooltipOpen = () => {
    const {catalog, showDetailsPopoverSettings: {interactionKind, ms}} = this

    if (interactionKind === 'hover') {
      // DEBUG && console.debug(`Opening popover for ${this.idWithPath} in ${ms}ms`)
      this._openPopoverTooltipTimeout && clearTimeout(this._openPopoverTooltipTimeout)

      this.showingOrHidingPopoverInMs = ms

      this._openPopoverTooltipTimeout = window.setTimeout(() => {
        catalog.hoverPopoverItemId = this.idWithPath
        this.showingOrHidingPopoverInMs = 0
      }, ms)
    } else {
      // DEBUG && console.debug(`This site is configured to only show popover details when this button is clicked.  To change this behavior, see ui.settings.cardPopover`)
    }
  }

  @action onPopoverTooltipClose = () => {
    const {catalog, showDetailsPopoverSettings: {interactionKind}} = this

    this._openPopoverTooltipTimeout && clearTimeout(this._openPopoverTooltipTimeout)
    this._openPopoverTooltipTimeout = 0

    // if (interactionKind === 'hover') { catalog.hoverPopoverItemId = null }
  }

  @computed get prototypicalCardSizeWhiteboard() {
    const {shapeStore, levelOfDetail, imagesToRender, catalog: {settings: {imageRatio}}} = this

    if (shapeStore?.imageSize && !shapeStore.shape.isGenerated) {
      return {width: shapeStore?.imageSize[0], height: shapeStore.imageSize[1]}
    }

    let r = _.cloneDeep(_.first(imagesToRender)?.originalSize ?? {width: 200, height: 200})

    r.width *= CARD_CREATE_SIZE_SCALING_FACTOR
    r.height *= CARD_CREATE_SIZE_SCALING_FACTOR

    return r
  }

  @computed get selectedImage() : BuilderCardImageStore {
    const {selectedImageIndex, imagesToRender} = this
    return _.isEmpty(imagesToRender) ? null : imagesToRender[selectedImageIndex]
  }

  @computed get prototypicalCardSize() {
    const {selectedImage, levelOfDetail, isInWhiteboard, catalog: {cardSizeSliderAsMultiplier, features: {card: {cardSizes, cardSizes: {landscape, square, portrait}}}, settings: {imageRatio}}} = this

    if (selectedImage?.overrideSize) return selectedImage.overrideSize

    if (isInWhiteboard) return this.prototypicalCardSizeWhiteboard
    let r = {width: 0, height: 0}

    const lodMultiplier = levelOfDetail === 1 ? cardSizes.lodMultiplier : 1

    switch (imageRatio) {
      case 'landscape': {
        r = {width: landscape.size * lodMultiplier, height: landscape.size * lodMultiplier * landscape.aspectRatio}
        break
      }

      case 'portrait': {
        r = {width: portrait.size * lodMultiplier * portrait.aspectRatio, height: portrait.size * lodMultiplier}
        break
      }

      case 'square':
      default: {
        r = {width: square.size * lodMultiplier, height: square.size * lodMultiplier}
        break
      }
    }

    return {width: r.width * cardSizeSliderAsMultiplier, height: r.height * cardSizeSliderAsMultiplier}
  }

  @computed get cardTitle() {
    const {catalog, catalog: {workspaceConfig}, data, props: {nested}} = this
    const cardTitle = data[workspaceConfig?.dataMap?.cardTitle]
    let r = nested?.data ? (nested.data.title ?? nested.data.name)
                         : (cardTitle ?? data?.title ?? data?.subtitle1 ?? data?.name ?? data?.exId ?? data.id)

    return r || catalog.catalogItemName
  }

  @computed get ws() {
    return this.workspaces.workspaceFor(this.workspace?._id)
  }

  @computed get workspace() {
    return this.props.workspace ?? this.catalog.workspaceConfig
  }

  @computed get activeField() {
    return this.catalog.activeFieldFor(this.workspace)
  }

  @computed get showToggleActive() {
    return this.activeField != null && this.catalog.isActionButtonEnabled('toggleActive')
  }

  get itemDetailsStore() { return this.props.itemDetailsStore}

  @action onToggleActive = async (e?: React.MouseEvent | React.TouchEvent<HTMLAnchorElement>, noRefetch = false) => {
    const
      {catalog, data, isActive, isOffline, catalog: {settings: {autoSaveInlineChanges}}} = this,
      {index} = catalog,
      field = catalog.activeFieldFor(catalog.workspaceConfig)

    await catalog.mutations.toggleBooleanField({index, field, data, value: isActive, noRefetch})
  }

  @action onSetActiveInActive = async (active: boolean, noRefetch = false) => {
    const
      {catalog, data, isOffline, catalog: {settings: {autoSaveInlineChanges}}} = this,
      {index} = catalog,
      field = catalog.activeFieldFor(catalog.workspaceConfig)

    await catalog.mutations.toggleBooleanField({index, field, data, value: !active, noRefetch})
  }

  @observable refreshingImages = false
  @action onRefreshImages = async () => {
    const {es: {reImportImage}, catalog, images} = this
    this.refreshingImages = true
    await Promise.all(images?.map(async ({image}) => {
      await reImportImage(image?.exId)
    }))
    this.refreshingImages = false
    catalog.refetch('catalogItems')
  }

  @action onAddNote = async (e: React.MouseEvent | React.TouchEvent<HTMLAnchorElement>) => {
    const {data, catalog} = this

    // Eat clicks on buttons on the card
    const t = e.currentTarget as HTMLElement
    !t.classList.contains(Classes.MENU_ITEM) && e.stopPropagation()
    e.stopPropagation()
    e.preventDefault()
    await catalog.notes.addNote(data as any)
  }

  @computed get idWithPath() {
    const {groupByStore, props: {data, nested}, catalog: {idFor, perspective}} = this

    const r = `${groupByStore ? `${groupByStore.pathLookup}${CATALOG_ID_SEPARATOR}` : ''}${nested?.parentData ? `${idFor(nested?.parentData)}${CATALOG_ID_SEPARATOR}` : ''}${nested?.data ? idFor(nested.data) : idFor(data)}`

    //console.log(toJS({r, parent, nestedObject, data}))

    return r
  }

  @computed get id() {
    const {props: {data, nested}, catalog: {perspective}} = this
    return nested?.data
           ? (nested.data[perspective?.idField] ?? nested.data?._id ?? nested.data?.exId ?? nested.data?.id)
           : data?._id ?? data?.exId ?? data?.id
  }

  @action onDelete = (e: React.MouseEvent | React.TouchEvent<HTMLAnchorElement>) => {
    const {id, catalog: {dialogs}} = this
    dialogs.showConfirmationProductPopup({id, message: `Are you sure you want to delete this item?`, operation: 'DELETE_PRODUCT'})

    //added this due to touch device issue - popup opens and closed immediatly
    if (e) {
      // Eat clicks on buttons on the card
      const t = e.currentTarget as HTMLElement
      !t.classList.contains(Classes.MENU_ITEM) && e.stopPropagation()

      e.stopPropagation()
      e.preventDefault()
    }
  }

  @action onDuplicate = async (e: React.MouseEvent | React.TouchEvent<HTMLAnchorElement>, force = false) => {
    const {
      props: {data}, catalog,
      catalog: {dialogs, features: {actionButtons: {duplicate}}, index, selection: {selectedIds}, catalogItemName}
    } = this

    // Eat clicks on buttons on the card
    if (e) {
      const t = e.currentTarget as HTMLElement
      !t.classList.contains(Classes.MENU_ITEM) && e.stopPropagation()
    }

    selectedIds.clear()

    if (!force && !_.isBoolean(duplicate) && duplicate.requireConfirm) {
      dialogs.showConfirmationProductPopup({
        skipConfirm: false,
        onConfirm: () => this.onDuplicate(null, true),
        message: <div className={css.duplicateConfirm}>
          <span className={css.title}>Duplicate {catalogItemName}?</span>
          <div className={css.cardContainer}>
            <BuilderCard {...this.props} disableSelection hideButtons isPopover={false} levelOfDetail={1} readOnly={true}/>
          </div>
        </div>
      })
    } else {
      return await catalog.createItem._duplicateCatalogItemById({index, id: data?.id ?? data?.exId ?? data?._id})
    }
  }

  @observable fieldsEverVisible = false

  /** Don't show buttons until user mouses in for first time
   * Hide buttons temporarily after user clicks qr code toggle */
  @observable hideButtons = true

  @computed get showHoverButtons() {
    const {props, hideButtons, dragDrop, isInWhiteboard} = this
    return isInWhiteboard
           ? false
           : !props.isPopover && !hideButtons && !props.hideButtons && dragDrop.dragItem?.id != this.id
  }

  @observable showQRCode = false

  // todo - do we need the checks other than in the dragDropState map?
  @computed get loading() {
    const {idWithPath, dragDropState, catalog: {createItem, settings}, refreshingImages} = this
    return refreshingImages || (dragDropState?.status === 'loading') || 
        (!settings.autoSaveInlineChanges && createItem.loader.has(idWithPath))
  }

  @action uploadToC8 = async () => {
    const {catalog} = this

    await catalog.createItem.createObjectInRemote(this.data)
    catalog.refetch()
  }

  @computed get success() {
    return this.dragDropState?.status === 'success'
  }

  @computed get isOffline() { return _.startsWith(this.id, OFFLINE_ID) && !ui.settings.noPublish }

  // @action onDragOver = e => {
  //   dragDrop.catalogItem_onDragOver(e)
  // }

  // @action onDrop = e => {
  //   const {catalog: {dragDrop: {dragType, dragItem}}} = stores,
  //     items = es.readField(this.data, dragType),
  //     isPresent = items?.val?.find(item => item.id === dragItem.id),
  //     shouldDropAttrib = items.val && dragItem.id === 'unassigned' || dragItem.id !== 'unassigned' && !isPresent
  //
  //   // If we don't have dragItem we set it from the dataTransfer if available.
  //   dragDrop.readDataTransfer(e)
  //   shouldDropAttrib && dragDrop.catalogItem_onDrop(e)
  // }

  @computed get isSelected() {
    const {groupByStore, catalog} = this
    return (!groupByStore && catalog.selectAll) || catalog.selection?.selectedIds?.has(this.idWithPath)
  }

  @computed get isCardFieldDragging() { return this.fieldDragState.draggingItem != null}

  @computed get groupsVisibleFields() {
    return Object.keys(this.catalog.cv.groupedVisibleCardFields)
  }

  @computed get perspective() {
    // Eventually allow individual groups to change to a perspective
    return this.catalog.perspective
  }

  @computed get perspectiveSectionConfig() {
    return this.catalog.sectionConfigFor(this.perspective?.id)
  }

  @computed get sectionGroupedFields() {
    const {visibleCardFields, catalog: {settings: {groupSectionFieldsOnCard}}} = this

    const sectionsToGroup = _.uniqBy(visibleCardFields.filter(f => f.section && groupSectionFieldsOnCard[f.section]), f => f.section)
    return sectionsToGroup.reduce((acc, f) => ({...acc, [f.section]: visibleCardFields.filter(v => v.section == f.section)}), {})
  }

  @computed get visibleCardFields(): Array<ICatalogFieldConfig> {
    const {selectedNestedItem, catalog} = this
    if (selectedNestedItem) {
      const {cv: {cardViewSelectedKeys}, _fieldByName} = catalog
      let field = selectedNestedItem.__path,
        childFields = cardViewSelectedKeys.filter(k => k.startsWith(field) && k != field)

      if (_.isEmpty(childFields)) {
        const fields = catalog.catalogConfig.fields.filter(f => f.field.startsWith(`${field}.`) && f.card?.default)
        return fields
      }

      return childFields.map(f => _fieldByName[f])
    }

    if (this.props.visibleCardFields) return this.props.visibleCardFields
    else if (this.props.nested) {
      // if (catalog.isShowingPerspective) {
      //   // todo - YFTS-7930
      //   return [] //catalog.visibleCardFieldsFor(this.props.nested.parentField, catalog.perspective.field) ?? []
      // }
      // else {
      //   throw new Error(`Nested card cannot be specified without including visibleCardFields`)
      // }

      return []
    }

    const {isCardFieldDragging, fieldDragState, fieldDragState: {draggingItem, dragOverItem}, tempCardFields} = this,
      {cv: {overriddenVisibleFields}} = catalog

    if (!isCardFieldDragging) return overriddenVisibleFields

    if (draggingItem && dragOverItem && draggingItem != dragOverItem) {
      const r = _.cloneDeep(tempCardFields ?? overriddenVisibleFields),
        fromIndex = r.findIndex(f => f.field == draggingItem),
        toIndex = r.findIndex(f => f.field == dragOverItem)

      if (fromIndex != -1 && toIndex != -1) {
        const item = r[fromIndex]

        // console.log(`Moving `, {r, fromIndex, toIndex})
        utils.array_move(r, fromIndex, toIndex)

        false && console.log(
          toJS({
            from: draggingItem,
            to: dragOverItem, fromIndex, toIndex,
            original: overriddenVisibleFields,
            lastTemp: tempCardFields,
            tempNew: r
          }, {recurseEverything: true}))

        this.tempCardFields = r
      }
    }

    return this.tempCardFields ?? overriddenVisibleFields
  }

  @observable fieldDragState: { draggingItem?: string, dragOverItem?: string, isAnimating?: boolean } = {}
  tempCardFields: Array<ICatalogFieldConfig>

  /**
   * Hooking into dragLeave for the card/container isn't good enough as it will trigger incorrectly due to us having drag targets INSIDE our card
   *
   * Instead the parent card panel hooks dragEnter and tells us via a reference in the CatalogStore
   *
   * This is hacky but I don't have a better solution
   */
  @action onDragIntoCatalog = () => {
    //console.log(`onDragIntoCatalog`)
    this.tempCardFields = null
    this.fieldDragState.dragOverItem = null
  }

  @action onStartReorderFieldAnimation = () => {
    this.fieldDragState.isAnimating = true
    DEBUG_CATALOG_ITEM_CARDS && console.log(`Start reorder animation`)
  }

  @action onFinishReorderFieldAnimation = () => {
    //console.log(`onFinishReorderFieldAnimation`)
    this.fieldDragState.isAnimating = false
    // if (this.fieldDragState.dragOverItemWhileAnimating) {
    //   this.fieldDragState.dragOverItem = this.fieldDragState.dragOverItemWhileAnimating
    //   this.fieldDragState.dragOverItemWhileAnimating = null
    // }
    DEBUG_CATALOG_ITEM_CARDS && console.log(`Stop reorder animation`)
  }

  specItemDragHandlers = (f: ICatalogFieldConfig, isNestedItem: boolean = false, def: IDragDropElement = null) => {
    let clone: HTMLDivElement

    const {catalog, dragDrop} = this
    return {
      onDragStart: (e: React.DragEvent) => {
        runInAction(() => {
          // e.preventDefault()
          e.stopPropagation()

          DEBUG_CATALOG_ITEM_CARDS && console.log(`specItem dragStart`)

          dragDrop.DragDropItem_onDragStart(e, def ?? {id: f.field, dragType: `Card Field: '${f.field}'`}, catalog, {setDragImage: false})
          e.dataTransfer.setData('BuilderCard/SpecificationItem', f.field)

          catalog.activeDragCardStore = this
          const dragItem = {id: def?.id ?? f.field, dragType: isNestedItem ? 'Nested Item' : 'BuilderCard/SpecificationItem', targetExtra: isNestedItem && def.targetExtra}
          DragDropStore._dragItem = dragItem

          // Replace the default drag placeholder with our own
          // console.log(`onDragStart`, f.field)
          const specItemNode = isNestedItem ? e.currentTarget.querySelector(`[data-type='VipImage']`) as HTMLDivElement ?? e.currentTarget.querySelector(`[data-type='NoImagePlaceholder']`) as HTMLDivElement : e.currentTarget.closest(`[data-type='SpecificationItem']`) as HTMLDivElement
          clone = specItemNode.cloneNode(true) as HTMLDivElement
          const {width, height} = specItemNode.getBoundingClientRect()
          if (isNestedItem && e.currentTarget) {
            clone.style.setProperty('border', `1px solid black`)
            clone.style.setProperty('width', `${width + 20}px`)
            clone.style.setProperty('height', `${height}px`)
          }
          document.body.appendChild(clone)
          clone.setAttribute('data-is-drag-placeholder', 'true')

          e.dataTransfer.setDragImage(clone, 0, 0)

          this.fieldDragState = {draggingItem: f.field}

          //clone.remove()
        })
      },
      onDragEnter: (e: React.DragEvent) => {
        const {fieldDragState, fieldDragState: {draggingItem, dragOverItem, isAnimating}} = this

        //console.log(`specItem dragEnter`, {draggingItem, field: f.field})

        e.stopPropagation()
        e.preventDefault()
        if (this.dragDrop.dragType !== 'Nested Item') {
          if (draggingItem == f.field || dragOverItem === f.field) {
            return
          }

          // console.log(`specItemDragHandlers.onDragEnter`, {dragOverItem, draggingItem, field: f.field, isAnimating})

          if (draggingItem) {
            //if (f.field == 'sketch') { debugger }
            if (!isAnimating && fieldDragState.dragOverItem != f.field) {
              //console.log(`Switch drag over item `, {from: fieldDragState.dragOverItem, to: f.field})
              fieldDragState.dragOverItem = f.field
            }
          }
        }
      },
      onDragLeave: (e: React.DragEvent) => {
        //console.log(`onDragLeave - spec item`, e.target, e.currentTarget)
      },
      // onDragOver: (e: React.DragEvent) => {
      //   // console.debug(`onDragOver`, this.debugInfo)
      //   console.log(`specItemDragHandlers.onDragOver`)
      //   if (this.draggingItem) {
      //     e.preventDefault()
      //     e.stopPropagation()
      //   }
      // },
      onDragEnd: (e: React.DragEvent) => {
        // console.log(`specItemDragHandlers.onDragEnd`)
        setTimeout(() => clone?.remove(), 1000)
        this.stopDraggingItem()
      },
      onDrop: (e: React.DragEvent) => {
        const {tempCardFields, visibleCardFields} = this
        DEBUG_CATALOG_ITEM_CARDS && console.log(`onDrop`, f.field, toJS(tempCardFields))
        runInAction(() => {
          setTimeout(() => clone?.remove(), 1000)
          if (this.dragDrop.dragType !== 'Nested Item') {
            if (tempCardFields) {
              catalog.settings.cardViewOrderOverride = Object.assign({}, (catalog.settings.cardViewOrderOverride ?? {}), {[catalog.perspectiveId]: visibleCardFields.map(f => f.field)})//console.log(toJS({override: catalog.settings.cardViewOrderOverride, tempCardFields, visibleCardFields}, {recurseEverything: true}))
            } else {
              console.log(`Did not spec item reorder drop due to tempCardFields being null`)
            }
          }
          this.stopDraggingItem()
        })
      }
    }
  }

  @action stopDraggingItem = () => {
    this.dragDrop.reset()
    this.fieldDragState = {}
    this.tempCardFields = null
  }

  @action onDropSpecOnRemove = () => {
    const {fieldDragState: {draggingItem}, catalog: {cv: {cardView}}} = this
    _.assign(cardView, {all: {show: false}, [draggingItem]: {show: false}})
    this.stopDraggingItem()
  }

  // @computed get specsToRender() {
  //   const {catalog: {libraryTypes}} = stores,
  //     {data, visibleCardFields} = this
  //
  //   return vlp.isVLPView ? visibleCardFields
  //                        : libraryTypes && Object.keys(libraryTypes).filter(k => {
  //     const {field: library} = libraryTypes[k]
  //
  //     if (visibleCardFields[library] == true || visibleCardFields[_.trimEnd(library, 's')] == true) {
  //       const specItem = data && data[library];
  //
  //       return (!specItem || specItem.isVisible)
  //     }
  //     return false
  //   })
  // }

  @computed get hideSpecs() {
    const {data} = this
    return !data ||
           !((data.materials && data.materials.isVisible)
           || (data.hardware && data.hardware.isVisible)
           || (data.styles && data.styles.isVisible)
           || (data.colors && data.colors.isVisible))
  }

  @computed get imageCount() {
    return this.images?.length ?? 0
  }

  @observable private _imageIndex = 0

  @action nextImage = () => {
    const {_imageIndex, imageCount} = this
    this._imageIndex = _imageIndex + 1 == imageCount ? 0 : _imageIndex + 1
  }

  @action previousImage = () => {
    const {_imageIndex, imageCount} = this
    this._imageIndex = _imageIndex > 0 ? _imageIndex - 1 : imageCount - 1
  }

  @computed get imagePaths() {
    return _.uniq(this.images.map(({image}) => image.__path))
  }

  @computed get images(): Array<BuilderCardImageStore> {
    const {catalog: {imagesForCatalogItem, settings, settings: {imageSource: {stackedCards}}}} = this, {data, imageStoreFor, props, props: {nested, workspace}} = this,
      imageSource = props.imageSource ?? settings.imageSource.view,
      imagesPerItem = 999 //imageSource === 'stack' || imageSource === 'grid' ? stackedCards.imagesPerItem : 999

    if (props.image) {
      return [imageStoreFor(props.image, 0)]
    }

    let r = imagesForCatalogItem({data: nested?.data ?? data, imageSource, workspace, takePer: imagesPerItem})
    if (_.isEmpty(r) && nested?.parentField) {
      r = imagesForCatalogItem({data: nested?.parentField, imageSource, workspace, takePer: imagesPerItem})
    }

    r = _.isArray(r) ? r : data?.images

    if (_.isBoolean(r)) {
      r = []
    }

    // trace()
    return _.uniqBy(r ?? [], v => v.exId ?? v.id ?? v.hex).map((v, i) => imageStoreFor(v, i))
  }

  @computed get lowDefImageSize() {
    const {catalog, levelOfDetail, props: {isPopover}} = this
    return catalog.catalogConfig?.defaultImageSize ?? (levelOfDetail == 1 && !isPopover ? 'smallImage' : 'smallImage')
  }

  @computed get highDefImageSize(): C8ImageSize {
    const {levelOfDetail} = this
    return 'largeImage'  //catalog.catalogConfig?.defaultImageSize ?? levelOfDetail == 1 ? 'largeImage' : 'smallImage'
  }

  @computed get stackedImagesStyle() {
    const {imageSourceView, imagesToRender, catalog} = this
    if (imageSourceView != 'stack' || _.isEmpty(imagesToRender) || imagesToRender.length <= 1 || !imagesToRender.some(v => v.size)) return {}

    // --card-width:  calc(var(--base-card-width) + (2 * var(--stack-overall-card-padding)) + (max(0, var(--image-count) - 1) * min(var(--image-source-stack-spread), 1) * (var(--base-card-width) + 4px)));  /*var(--image-source-stack-spread)*/
    const right = _.last(imagesToRender).right
    const {settings: {imageSource: {stackedCards: {spread}}}} = catalog,
      cardWidth = _.isNaN(right) ? undefined : `${(right)}px`

    //console.log(`stackedImagesStyle`, {cardWidth, right, imagesToRender})
    return {
      '--card-width': cardWidth
    }
  }

  @observable allImagesLoaded = false

  stackedImageStyleForItem = computedFn((index: number) => {
    const {imageSourceView, imagesToRender} = this
    if (imageSourceView != 'stack' || _.isEmpty(imagesToRender)) return {}

    const image = imagesToRender[index]
    return image.offsetLeft ?
      {
        '--x': `${image.offsetLeft}px`,
        '--image-width': `${image.width}px`
      } : {}
  })

  @computed get imagesDivStyle() {
    const {imagesToRender, imageSourceView} = this
    return ({
      '--item-count': imagesToRender.length ?? 0,
      '--total-card-width': imageSourceView === 'stack' ? `${this.totalImagesStackWidth}px` : undefined
    } as any)
  }

  @computed get totalImagesStackWidth() {
    const {images} = this
    return _.sum(images.map(i => i.width))
  }

  @computed get imagesToRender(): Array<BuilderCardImageStore> {
    return (this.images ?? []).filter(_.identity)
  }

  @computed get imageUrl(): ImageUrlWithHD {
    const {images, data, levelOfDetail, _imageIndex, props: {nested}, catalog: {settings: {imageRatio}}} = this

    //console.log(toJS({images, nested, data, nestedObject}, {recurseEverything: true}))
    const {firstImage} = data

    if (_.isEmpty(images) && _.isEmpty(firstImage)) {
      if (!nested && data.hex) {
        return {defaultImageUrl: data.hex}
      } else if (nested?.data?.hex) {
        return {defaultImageUrl: nested.data.hex}
      }

      return null
    }

    const selectedImage: BuilderCardImageStore = images?.length > 0 ? _imageIndex ? images[_imageIndex] : _.first(images)
                                                                    : _.first(firstImage) ?? ((images && images.length > 0) ? images[_imageIndex] : _.first(images))

    if (selectedImage) {
      return selectedImage.cardImageData.imageUrl
    }

    return null
    // console.debug(`Dead code?`)
    // //console.log({images, _imageIndex})
    // const {catalog: {imageUrlFor}, shapeStore, stores: {cantaloupe: {normalizeImageSize}}, isInWhiteboard, lowDefImageSize, highDefImageSize} = this,
    //   size = levelOfDetail ? 200 : 100,
    //   imageSize = normalizeImageSize(shapeStore?.imageSize ?? {width: imageRatio === 'landscape' ? size * 1.33 : size, height: imageRatio === 'portrait' ? size * 1.33 : size})
    //
    // // If we do largeImage here it breaks native D&D image preview in chrome
    // const lowDef = _.isString(selectedImage) ? selectedImage : imageUrlFor(selectedImage, {c8Size: lowDefImageSize, imageSize})
    // const image: any = images[_imageIndex]
    // const highDef = !image ? lowDef : imageUrlFor(image, {c8Size: highDefImageSize, imageSize})
    //
    // return {defaultImageUrl: lowDef, highDefUrl: highDef}
  }

  @observable unstackCards = false
  @action onToggleUnstackCards = () => {
    this.unstackCards = !this.unstackCards
  }

  @computed get localData() {
    return this.catalog.settings.perItem[this.idWithPath]
  }

  @computed get localLOD() {
    return this.localData?.lod ?? this.props.levelOfDetail
  }

  set localLOD(v) {
    this.updateLocalData({lod: v})
  }

  @action updateLocalData = (changes?: Partial<BuilderCardLocalData>) => {
    const {localData, idWithPath, catalog} = this
    catalog.settings.perItem[idWithPath] = _.merge(
      {},
      localData,
      changes
    )
  }
  @action toggleLocalLOD = (e: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }

    const {updateLocalData, localLOD, globalLevelOfDetail, props, isSelected, catalog: {selection, settings: {levelOfDetail: settingsLOD}}} = this

    if (isSelected) {
      selection.toggleSelectedInlineDetails()
    } else {
      updateLocalData({
        lod: localLOD === undefined || localLOD === null
             ? _.isUndefined(props.levelOfDetail)
               ? globalLevelOfDetail === 1 ? 0 : 1
               : (props.levelOfDetail !== 1 ? 1 : 0)
             : localLOD === 0 ? 1 : 0
      })
    }
  }

  @computed get qrCodeUrl() {
    if (!this.showQRCode) return null

    return null
    // const {nav: {params, queryParams: qp}, site: {urlFor}} = stores, {idWithPath} = this
    // let url = `${window.location.origin}${urlFor('Products', params)}?${utils.stringifyQs({product: idWithPath, groups: qp.groupBy})}`
    // return url
  }

  @computed get isActive() {
    const {es, props: {nested}, catalog: {perspective}, stores: {deployments: {isNewSync}}} = this
    let active = _.get(this.data, perspective?.activeField || isNewSync? 'Active': 'attributes.Active')
    if (nested?.data) {
      if (perspective?.activeField) {
        active = es.readField(nested.data, perspective?.activeField).val
      }
    }
    return isNewSync? String(active).toLowerCase() !== "false" : active !== false
  }

  @computed get isShowingInModal() {
    const {stores: {sidebar: {modalViewerItemId}}} = this
    return modalViewerItemId === this.idWithPath
  }

  @computed get areAllFieldsValid(): boolean {
    const {catalog, data, isOffline} = this

    return !isOffline || _.isEmpty(catalog.validateCatalogItem(this.data))
  }

  @computed get isFocused() {
    const {catalog} = this,
      r = //this.isShowingInModal ||
        (this.idWithPath && this.idWithPath === catalog.focusedCatalogItemId)

    return r
  }

  @computed get seasonsValueToNavToItem() : Record<CatalogFieldName, FilterLabel> {
    const {seasonSelectorFieldsNonNull, data} = this

    return seasonSelectorFieldsNonNull.reduce((acc, {field}) => {
      let val = _.get(data, `${field}.name`) ?? _.get(data, field)
      if (Array.isArray(val)) {
        val = val.map(v => _.get(v, 'name')).join(' | ')
      }

      if (val && val !== '') {
        acc[field] = val
      }
      return acc
    }, {})
  }

  @computed get seasonSelectorFieldsNonNull() {
    const {props: {data}, vlp, catalog: {seasonSelectorFields}, stores: {filter: {state: {groups}}}} = this

    if (!data || _.isEmpty(seasonSelectorFields)) return []

    // If any of the season filters don't include this object, show the button
    const r = seasonSelectorFields.filter(({field: f}) => {
      // TODO: Need to add subcategories logic in the config.
      let fieldVal = (_.get(data, `${f}.id`) ?? _.get(data, `${f}.exId`) ?? data?.subCategories),
        fieldName = _.get(data, `${f}.name`)
      if (_.isArray(fieldVal)) {
        fieldVal = !_.isEmpty(data?.subCategories) ? (data?.subCategories[0].id ?? data?.subCategories[0].exId) : null
      }
      if (!fieldVal || !fieldName) {
        return false
        //console.log(`Unable to find ${field} value on `, toJS(data))
      } else if (!groups[f]) {
        //console.log(`Unable to find filter group for field ${f}`)
      }

      // Filter out items we already know due to our existing hierarchy selections
      return fieldVal && !(groups[f]?.selected?.has(fieldName) || vlp?.hierarchy?.isSelected(f, fieldVal))
    }).filter(_.identity)

    return r
  }

  @action navigateToSeason = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    const {data, catalog: {navigateToHierarchy}} = this

    navigateToHierarchy(data)

    setTimeout(() => {
      runInAction(() => {
        const {catalog: {selection: {selectedIds}}} = this
        selectedIds.clear()
        selectedIds.add(this.idWithPath)
      })
    }, 10)
  }

  convertToDataURL = (dataURL, renderWidth, renderHeight) => {
    Object.assign(this.dragDropEventHandlers.def, {imageDataUrl: dataURL, imageWidth: renderWidth, imageHeight: renderHeight})
  }

  /**
   * For developers - copy a string to the clipboard to get this specific catalog item in kibana
   */
  @observable copyDbString = false

  /**
   * YFTS-7902
   */
  @computed get matchesHoverItem() {
    const {groupByStore, assortmentCard} = this
    if (assortmentCard?.hoverItem) {
      return assortmentCard.hoverItem.matchesHoverItem(this)
    }

    if (!groupByStore?.hasSwatchHoverContext) return undefined

    return groupByStore.matchesGroupHoverItem(this.idWithPath)
    // console.log(toJS({r, items: groupByStore.matchingCatalogItemsForSwatchHoverSelect, id: this.idWithPath}))
  }

  @observable.ref contextItem: { data: any, meta?: any, imageUrl?: string }

  @computed get contextItemField() {
    const {contextItem, catalog: {_fieldByName}} = this

    return _fieldByName[contextItem?.data?.__path]
  }

  ignoreNextContextMenuBubble = false

  @action onContextMenu = (contextItem: { data: any, meta?: any, imageUrl?: string }) => (e: React.MouseEvent) => {
    const {catalog, catalog: {hoverPopoverItemId}, idWithPath, contextMenu} = this

    // Close any existing card popovers if they aren't this one
    if (hoverPopoverItemId != idWithPath) {
      catalog.hoverPopoverItemId = null
    }

    if (contextItem) {
      this.ignoreNextContextMenuBubble = true
    }

    if (!contextItem && this.ignoreNextContextMenuBubble) {
      this.ignoreNextContextMenuBubble = false
      return
    }

    this.contextItem = contextItem
    contextMenu.onComponentContextMenu(e, this)
  }

  @computed get isIncludedInMultiSelect() {
    const {idWithPath, catalog: {selectAll, selection: {selectedIds}}} = this
    if (!selectAll && !selectedIds.size) return false

    return selectAll || (selectedIds.size > 1 && selectedIds.has(idWithPath))
  }

  @computed get publishValidationFailures() {
    const {data, catalog: {validateCatalogItem}} = this
    return validateCatalogItem(data)
  }

  @computed get canPublish() {
    const {isEditable} = this
    return isEditable && _.isEmpty(this.publishValidationFailures)
  }

  @computed get isEditable() {
    const {catalog, data} = this
    return catalog.isCatalogItemEditableByUser({item: data})
  }

  @computed get esIndex() {
    const {props: {workspace}, catalog} = this
    return workspace?.catalog?.index ?? catalog.index
  }

  @computed get objectFit() {
    const {props: {nested}, catalog} = this
    if (nested?.field?.esIndex) {
      const ws = catalog.workspaceByIndex[nested.field.esIndex]
      return ws?.catalog?.features?.card?.objectFit ?? 'contain'
    }

    const {props: {workspace}} = this
    return workspace ? catalog.objectFitFor(workspace) : catalog.objectFit
  }

  @observable.ref hoverSubItem: BuilderCardImageStore

  @computed get showPopover() {
    return this.catalog.hoverPopoverItemId === this.idWithPath
    /* && this.hoverSubItem == null */ // We don't want the whole inline popover to disappear whenever they mouse over a sub image, we just want to show a tooltip
  }

  @computed get imageSourceView(): CatalogCardImagesView {
    const {props: {isPopover}, catalog: {imageSource}} = this
    if (isPopover || this.images.length <= 1) return 'first'
    return imageSource.view ?? 'first'
  }

  /**
   * YFTS-8177 Image Carousel
   */
  @observable selectedImageIndex = 0

  /**
   * When loading in images from paste we want to scroll the image into view once it is fully loaded
   */
  @observable selectNextLoadedImage = false

  @computed get maxAllowedSelectedImageIndex() { return Math.max(0, this.imagesToRender.length - 1)}

  @action selectPreviousImage = () => {
    const {selectedImageIndex, maxAllowedSelectedImageIndex} = this
    this.selectedImageIndex = selectedImageIndex == 0 ? maxAllowedSelectedImageIndex : selectedImageIndex - 1
  }

  @action selectNextImage = () => {
    const {selectedImageIndex, maxAllowedSelectedImageIndex} = this
    this.selectedImageIndex = selectedImageIndex >= maxAllowedSelectedImageIndex ? 0 : selectedImageIndex + 1
  }

  copyDocQueryToClipboard = () => {
    const {catalog: {index}, id} = this
    navigator.clipboard.writeText(`GET ${index}/_doc/${id}\n`)
    this.copyDbString = true
    setTimeout(() => this.copyDbString = false, 2000)
  }

  @action showFullScreenImage = () => {
    const {} = this
    this.toggleCatalogItemDetails(null, true, true)
  }

  @computed get tooltipTitle() {
    const {cardTitle, workspace} = this
    return `${cardTitle}\n(${workspace.itemName})`
  }
}




// WEBPACK FOOTER //
// ./src/modules/catalog/product/productCard/components/BuilderCard/BuilderCardStore.tsx